import { LoginExtraHeaders, type LoginExtraParams } from "@/core/user/consts";
import apiClient from "../apiClient";

import type { LoginApp, User, UserToken } from "@/core/user/userEntity";

type SignInParams = {
	username: string;
} & typeof LoginExtraParams;
type SignInData = {
	password: string;
};
interface UserInfoData {
	sysUser: User;
	permissions: string[];
	roles: string[];
}

// 系统用户相关类型定义
export interface UserVO {
	/** 主键ID */
	userId: string;
	/** 用户名 */
	username: string;
	/** 姓名 */
	name?: string;
	/** 头像 */
	avatar?: string;
}

export interface UserPageQueryDTO {
	/** 页码 */
	current?: number;
	/** 页面大小 */
	size?: number;
	/** 用户名搜索 */
	username?: string;
}

export interface UserPageVO {
	/** 总数 */
	total: number;
	/** 当前页 */
	current: number;
	/** 页面大小 */
	size: number;
	/** 用户列表 */
	records: UserVO[];
}

export enum UserApi {
	SignIn = "/auth/oauth2/token",
	Logout = "/auth/token/logout",
	UserInfo = "/admin/user/info",
	GetLoginAppList = "/admin/social/getLoginAppList",
	GetUserPage = "/admin/user/page",
	GetAllUserPage = "/admin/user/center/page",
	CaptchaCreate = "/admin/sysMessage/captcha/create",
	CaptchaCheck = "/admin/sysMessage/captcha/check",
	SendSmsCode = "/admin/sysMessage/send/smsCode",
}

/** 滑块验证码数据 */
export interface SliderCaptchaData {
	/** 背景图片 base64 */
	originalImageBase64: string;
	/** 滑块图片 base64 */
	jigsawImageBase64: string;
	/** 验证码token */
	token: string;
	/** 滑块y坐标（可选，有些实现需要） */
	yHeight?: number;
}

/** 滑块验证码创建响应 */
export interface SliderCaptchaResult {
	repData: SliderCaptchaData;
}

/** 滑块验证码校验参数 */
export interface SliderCaptchaCheckParams {
	/** 验证码token */
	token: string;
	/** 滑动轨迹点坐标 JSON 字符串 */
	pointJson: string;
}

/** 滑块验证码校验响应 */
export interface SliderCaptchaCheckResult {
	/** 验证凭证 */
	captchaVerification: string;
}

/** 发送短信验证码参数 */
export interface SendSmsParams {
	/** 手机号 */
	mobile: string;
	/** 验证凭证 */
	captchaVerification: string;
}

const signin = ({ params, data }: { params: SignInParams; data: SignInData }) =>
	apiClient.post<UserToken>({
		url: UserApi.SignIn,
		headers: LoginExtraHeaders,
		params,
		data,
	});

const signinWithWechat = (code: string) => {
	const params = {
		grant_type: "mobile",
		mobile: `WECHAT_OPEN@${code}`,
		scope: "server",
	};
	return apiClient.post<UserToken>({
		url: UserApi.SignIn,
		headers: LoginExtraHeaders,
		params,
	});
};

const signinWithSms = (mobile: string, code: string) => {
	const params = {
		grant_type: "mobile",
		mobile: `SMS@${mobile}`,
		code,
		scope: "server",
	};
	return apiClient.post<UserToken>({
		url: UserApi.SignIn,
		headers: {
			Authorization: LoginExtraHeaders.Authorization,
			"Content-Type": "application/x-www-form-urlencoded",
		},
		params,
	});
};
const logout = () => apiClient.delete({ url: UserApi.Logout });
const getUserInfo = () =>
	apiClient.get<UserInfoData>({ url: UserApi.UserInfo });

const getLoginAppList = () =>
	apiClient.get<LoginApp[]>({ url: UserApi.GetLoginAppList });

/** 获取系统用户分页列表 */
const getUserPage = (params: UserPageQueryDTO) =>
	apiClient.get<UserPageVO>({ url: UserApi.GetUserPage, params });

/**
 * 把响应 JSON 文本中的 userId / id 大整数字段加上引号再解析，
 * 避免 JS Number 精度丢失（>2^53 的雪花 ID 场景）
 */
export const parseUserPageResponse = (raw: unknown) => {
	if (typeof raw !== "string") return raw;
	const quoted = raw.replace(
		/"(userId|id|accountNo)"\s*:\s*(-?\d{16,})/g,
		'"$1":"$2"',
	);
	try {
		return JSON.parse(quoted);
	} catch {
		return JSON.parse(raw);
	}
};

/** 获取系统用户分页列表 */
const getAllUserPage = (params: {
	current: number | any;
	size: number | any;
	keyword: string | undefined | any;
}) =>
	apiClient.post<UserPageVO>({
		url: UserApi.GetAllUserPage,
		data: params,
		transformResponse: [parseUserPageResponse],
	});

export interface ExecutorUser {
	userId: string;
	userName?: string;
	accountNo?: string;
	avatar?: string;
	userType?: string;
}

export interface ExecutorSearchParams {
	keyword?: string;
	current?: number;
	size?: number;
	includeOperator?: boolean;
}

export interface ExecutorPageVO {
	total: number;
	current: number;
	size: number;
	records: ExecutorUser[];
}

/** 搜索可执行任务的用户（马甲号） - 支持分页 */
const searchExecutor = (params: ExecutorSearchParams) =>
	apiClient.get<ExecutorPageVO>({
		url: "/admin/user/center/executor/search",
		params,
		transformResponse: [parseUserPageResponse],
	});

/** 批量查询执行用户详情 */
const getExecutorByIds = (userIds: string[]) =>
	apiClient.post<ExecutorUser[]>({
		url: "/admin/user/center/executor/batch",
		data: userIds,
		transformResponse: [parseUserPageResponse],
	});

/** 随机获取N个执行用户（马甲号） */
const getRandomExecutors = (count: number) =>
	apiClient.get<ExecutorUser[]>({
		url: "/admin/user/center/executor/random",
		params: { count },
	});

/** 用户详情类型 */
export interface UserDetail {
	userId: string;
	userName?: string;
	accountNo?: string;
	avatar?: string;
	userType?: string;
	lockFlag?: string;
	age?: number;
	country?: string;
	followCount?: number;
	fansCount?: number;
	postCount?: number;
	collectCount?: number;
	personal?: {
		sex?: string;
		anonymityAvatar?: string | any;
		certType?: string;
		/** 用户身份码 0-学生 1-家长 2-从业人员，对应 UserRoleEnums */
		feature?: string;
	};
	userSchoolLabels?: Array<{
		schoolId: string;
		schoolName: string;
		hasReading?: boolean;
	}>;
	recentPostLimit?: Array<{
		imageInfos?: Array<{
			imageUrl?: string;
		}>;
		videoInfo?: {
			coverImage?: {
				imageUrl?: string;
			};
		};
	}>;
}

/** 获取用户详情 */
const getUserDetail = (userId: string) =>
	apiClient.get<UserDetail>({ url: `/admin/user/center/${userId}` });

/** 创建滑块验证码 */
const createCaptcha = () =>
	apiClient.get<SliderCaptchaResult>({ url: UserApi.CaptchaCreate });

/** 校验滑块验证码 */
const checkCaptcha = (params: SliderCaptchaCheckParams) =>
	apiClient.post<SliderCaptchaCheckResult>({
		url: UserApi.CaptchaCheck,
		data: params,
	});

/** 发送短信验证码 */
const sendSmsCode = (params: SendSmsParams) =>
	apiClient.post({ url: UserApi.SendSmsCode, data: params });

/** 「重新发送」倒计时兜底秒数，与服务端 SmsParamConstants.SMS_CODE_INTERVAL_SECONDS_DEFAULT 一致。 */
export const DEFAULT_SMS_RESEND_INTERVAL_SECONDS = 60;

/**
 * 取「重新发送」倒计时秒数。
 *
 * 读运营参数 SMS_CODE_INTERVAL_SECONDS，与服务端发送间隔同源。
 * App 走 /config 下发，网页端没有那条通道，直接读这个免鉴权公开参数接口。
 * 取不到就用兜底——倒计时只是不让用户空点，真正的闸在服务端。
 */
const getSmsResendIntervalSeconds = async (): Promise<number> => {
	try {
		const raw = await apiClient.get<unknown>({
			url: "/admin/param/publicValue/SMS_CODE_INTERVAL_SECONDS",
		});
		const seconds = Number(raw);
		return Number.isFinite(seconds) && seconds > 0
			? seconds
			: DEFAULT_SMS_RESEND_INTERVAL_SECONDS;
	} catch {
		return DEFAULT_SMS_RESEND_INTERVAL_SECONDS;
	}
};

export default {
	signin,
	signinWithWechat,
	signinWithSms,
	logout,
	getUserInfo,
	getLoginAppList,
	getUserPage,
	searchExecutor,
	getExecutorByIds,
	getRandomExecutors,
	getUserDetail,
	createCaptcha,
	checkCaptcha,
	sendSmsCode,
	getSmsResendIntervalSeconds,
	getAllUserPage,
};
